/**
 * Timezone utility functions for handling timezone information in API calls
 * Ensures consistent timezone handling across all date-related operations
 */

import { DateTime } from "luxon";
import moment from "moment-timezone";
import logger from "./logger";

const PREFERRED_TIMEZONES = {
  GH: "Africa/Accra",
  KE: "Africa/Nairobi",
  NG: "Africa/Lagos",
  ZA: "Africa/Johannesburg",
  EG: "Africa/Cairo",
  TZ: "Africa/Dar_es_Salaam",
  UG: "Africa/Kampala",
  RW: "Africa/Kigali",
  ET: "Africa/Addis_Ababa",
  US: "America/New_York",
  GB: "Europe/London",
  FR: "Europe/Paris",
  DE: "Europe/Berlin",
  AE: "Asia/Dubai",
  IN: "Asia/Kolkata",
  SG: "Asia/Singapore",
  JP: "Asia/Tokyo",
  CA: "America/Toronto",
  AU: "Australia/Sydney",
};

/**
 * Automatically determine the most appropriate timezone for a given country code.
 *
 * @param {string} countryCode - 2-letter ISO country code (e.g. "GH", "KE", "US")
 * @param {string} [preferredZone] - Optional preferred timezone to preserve if valid
 * @returns {string|null} IANA timezone identifier
 */
export const getTimezoneForCountry = (countryCode, preferredZone = null) => {
  if (!countryCode || typeof countryCode !== "string") return null;
  const code = countryCode.trim().toUpperCase();
  if (PREFERRED_TIMEZONES[code]) return PREFERRED_TIMEZONES[code];

  try {
    const zones = moment.tz.zonesForCountry(code);
    if (zones && zones.length > 0) {
      if (preferredZone && zones.includes(preferredZone)) return preferredZone;
      const userTz = getUserTimezone();
      if (zones.includes(userTz)) return userTz;
      return zones[0];
    }
  } catch (error) {
    logger.warn(`Could not lookup timezone for country code ${code}:`, error);
  }
  return null;
};


/**
 * Get the user's current timezone from the browser
 * Falls back to UTC if the browser doesn't support timezone detection
 *
 * @returns {string} IANA timezone string (e.g., "America/New_York", "Europe/London")
 */
export const getUserTimezone = () => {
  try {
    // Get timezone from browser's Intl API
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Validate that we got a valid timezone string
    if (timezone && typeof timezone === "string") {
      return timezone;
    }

    // Fallback to UTC if something went wrong
    logger.warn("Could not detect user timezone, falling back to UTC");
    return "UTC";
  } catch (error) {
    // If Intl API is not supported (very rare in modern browsers)
    logger.error("Error detecting timezone:", error);
    return "UTC";
  }
};

/**
 * Get the current UTC offset in minutes for the user's timezone
 * Handles DST automatically using Luxon
 *
 * @returns {number} Current UTC offset in minutes (e.g., -300 for EST, -240 for EDT)
 */
export const getCurrentTimezoneOffset = () => {
  try {
    return DateTime.now().offset; // Returns current offset in minutes
  } catch (error) {
    logger.error("Error getting current timezone offset:", error);
    return 0;
  }
};

/**
 * Get the UTC offset in minutes for a specific date/time in a timezone
 * Handles DST automatically using Luxon
 *
 * @param {Date|string} dateTime - Date/time to get offset for
 * @param {string} timezone - IANA timezone string (defaults to user's timezone)
 * @returns {number} UTC offset in minutes (e.g., -300 for EST, -240 for EDT)
 */
export const getTimezoneOffsetForDate = (dateTime, timezone = getUserTimezone()) => {
  try {
    const dt = DateTime.fromJSDate(new Date(dateTime), { zone: timezone });
    return dt.offset; // Returns offset in minutes
  } catch (error) {
    logger.error("Error getting timezone offset:", error);
    return 0;
  }
};

/**
 * Convert a local date/time to UTC for API calls
 * Handles DST automatically using Luxon
 *
 * @param {Date|string} localDateTime - Local date/time to convert
 * @param {string} timezone - IANA timezone string (defaults to user's timezone)
 * @returns {string} UTC ISO string for API calls (pure UTC, no offset attached)
 */
export const convertToUTC = (localDateTime, timezone = getUserTimezone()) => {
  try {
    const dt = DateTime.fromJSDate(new Date(localDateTime), { zone: timezone });
    return dt.toUTC().toISO();
  } catch (error) {
    logger.error("Error converting to UTC:", error);
    return new Date(localDateTime).toISOString();
  }
};

/**
 * Convert UTC date/time from API to user's local timezone for display
 *
 * @param {string} utcDateTime - UTC ISO string from API
 * @param {string} timezone - IANA timezone string (defaults to user's timezone)
 * @returns {Date} Local date object for display
 */
export const convertFromUTC = (utcDateTime, timezone = getUserTimezone()) => {
  try {
    const dt = DateTime.fromISO(utcDateTime, { zone: "utc" });
    return dt.setZone(timezone).toJSDate();
  } catch (error) {
    logger.error("Error converting from UTC:", error);
    return new Date(utcDateTime);
  }
};

/**
 * Get timezone offset in hours from UTC
 * Useful for display purposes
 *
 * @returns {number} Offset in hours (e.g., -5, +3, 0)
 */
export const getTimezoneOffset = () => {
  try {
    const offset = new Date().getTimezoneOffset();
    // getTimezoneOffset returns minutes with inverted sign
    // (negative for ahead of UTC, positive for behind UTC)
    return -offset / 60;
  } catch (error) {
    logger.error("Error getting timezone offset:", error);
    return 0;
  }
};

/**
 * Format timezone for display
 *
 * @param {string} timezone - IANA timezone string
 * @returns {string} Formatted timezone display (e.g., "EST", "GMT+3")
 */
export const formatTimezoneDisplay = (timezone = null) => {
  try {
    const tz = timezone || getUserTimezone();
    const now = new Date();

    // Get the short timezone abbreviation
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    });

    const parts = formatter.formatToParts(now);
    const timeZonePart = parts.find(part => part.type === "timeZoneName");

    return timeZonePart ? timeZonePart.value : tz;
  } catch (error) {
    logger.error("Error formatting timezone:", error);
    return timezone || "UTC";
  }
};

const timezoneUtils = {
  getUserTimezone,
  getTimezoneForCountry,
  getTimezoneOffset,
  getCurrentTimezoneOffset,
  getTimezoneOffsetForDate,
  formatTimezoneDisplay,
  convertToUTC,
  convertFromUTC,
};

export default timezoneUtils;
